/**
 * Pure aggregation of a bulk-move's per-item outcomes.
 *
 * Bulk move is a per-item loop, so some items can succeed while others fail
 * (e.g. a name conflict at the destination). This summarises the loop into the
 * counts + failed set the UI needs for the "N moved, M failed" report; the
 * caller does i18n/formatting of the actual toast.
 *
 * Outcomes are reported by **entry id** (STD-029 R-2), with the display path
 * carried alongside for the message only. Reporting failure by path was a real
 * defect: two rows can share one display path — the dual-entry case B-1 exists
 * to make separately selectable — so when one twin moved and the other failed,
 * a path-keyed answer re-selected BOTH, telling the user to retry a move that
 * had already succeeded.
 */
import { FileItem } from './types';
import { entryId, entryIds } from './entryIdentity';

export interface MoveOutcome {
  /** The entry's identity — what the retry-selection is rebuilt from. */
  id: string;
  /** The entry's display path — for the failure message's names, never for matching. */
  key: string;
  success: boolean;
  error?: string;
}

export interface MoveSummary {
  movedCount: number;
  /** Entry ids that failed — these stay selected so the user can retry. */
  failedIds: string[];
  failed: { id: string; key: string; error?: string }[];
}

export function summarizeMoveResults(results: readonly MoveOutcome[]): MoveSummary {
  const failed = results
    .filter((r) => !r.success)
    .map((r) => ({ id: r.id, key: r.key, error: r.error }));
  return {
    movedCount: results.filter((r) => r.success).length,
    failedIds: failed.map((f) => f.id),
    failed,
  };
}

/**
 * The selection to restore after a bulk move: exactly the dragged entries that
 * failed, so the user can retry them and the ones that moved drop out.
 *
 * Intersecting with `items` rather than trusting `failedIds` wholesale is the
 * guard: the ids come back across the `onMoveMany` boundary, and a selection is
 * only ever allowed to name something that was actually in this batch. Returns
 * a Set because that is what `useFileSelection.setSelected` takes.
 */
export function selectionAfterMove(
  items: readonly FileItem[],
  failedIds: readonly string[]
): Set<string> {
  const failed = new Set(failedIds);
  return new Set(entryIds(items).filter((id) => failed.has(id)));
}

/** Build the outcome record for one attempted move. */
export function moveOutcome(item: FileItem, success: boolean, error?: string): MoveOutcome {
  return { id: entryId(item), key: item.key, success, error };
}
